import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import getSymbolFromCurrency from 'currency-symbol-map';
import { format, getYear, parseISO } from 'date-fns';
import Text from 'design-system/Text';
import Amount from 'design-system/Amount';
import Button from 'design-system/Button';
import { rem } from 'design-system/values';
import BlurAmountView from 'design-system/BlurAmountView';
import useColors from 'hooks/useColors';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import { useAppSelector } from 'store/hooks';
import { selectAccountById, selectFeatureFlag } from 'reducers/selectors';
import { SpaceUserAvatar } from 'design-system/SpaceUserAvatar';
import { isWeb } from '../../../constants';
import TransactionLogo from './TransactionLogo';
import EditTransactionSelect from './multiple/EditTransactionSelect';
import TransactionAutoCreated from './detail/TransactionAutoCreated';
import { TransactionBankLogo } from './TransactionBankLogo';
const RowTransaction = ({ item, switchingButon, disabled, selected, onPress, style, btnStyles, backgroundDark, isEditingTransactions, showUpgradeButton, showDate, showCategory = true, categoryPosition = 'right', datePosition = 'left', blurValue, isMultipleSelect, activeOpacity = 1, }) => {
    const bankLogoTransaction = useAppSelector((store) => selectFeatureFlag(store, 'bank_logo_transaction')).value;
    const colors = useColors();
    const isCryptoAccount = useAppSelector((store) => selectAccountById(store, item.accountId)?.type === 'CRYPTO');
    const onRowPressed = useCallback(() => {
        onPress?.(item);
    }, [item, onPress]);
    const renderSwitching = () => {
        if (disabled || isWeb)
            return null;
        return switchingButon;
    };
    const { counterpartName, customName, amount, nativeAmount, notes, currency } = item;
    const feeAmount = 'feeAmount' in item ? item.feeAmount : undefined;
    let fee = null;
    if (feeAmount && feeAmount !== amount)
        fee = Math.abs(feeAmount);
    let firstBalance = amount;
    let firstCurrency = currency;
    let secondBalance = null;
    let secondCurreny = null;
    const isCrypto = isCryptoAccount && Boolean(nativeAmount);
    if (nativeAmount && isCrypto) {
        secondBalance = nativeAmount.amount;
        secondCurreny = null;
    }
    else if (nativeAmount) {
        firstBalance = nativeAmount.amount;
        firstCurrency = nativeAmount.currency;
        secondBalance = amount;
        secondCurreny = currency;
    }
    const cryptoAmountProps = isCrypto
        ? {
            fractionDigits: null,
            numberOfLines: 1,
            ellipsizeMode: 'tail',
        }
        : undefined;
    const date = useMemo(() => {
        if (showDate) {
            const dateToDisplay = item.customDate || item.bookingDate;
            const showYear = getYear(parseISO(dateToDisplay)) !== getYear(new Date());
            return format(parseISO(dateToDisplay), showYear ? 'd MMM yyyy' : 'd MMM');
        }
        return undefined;
    }, [showDate, item]);
    const { isUnlockedForSpace, onPressUpgrade } = useShouldShowUpgrade({
        // Note we are not checking rename transaction OR offlineAccounts, optimisation as they are currently on the same tier
        benefitId: 'split',
    });
    const nameToDisplay = isUnlockedForSpace
        ? customName || counterpartName
        : counterpartName;
    const hasUpgradeButton = showUpgradeButton &&
        !isUnlockedForSpace &&
        (('splitFromId' in item && item.splitFromId != null) ||
            ('provider' in item && item.provider === 'MANUAL'));
    return (<TouchableHighlight onCard hoverOnLight style={btnStyles} activeOpacity={activeOpacity} disabled={disabled} onPress={onRowPressed} underlayColor={colors.background.underlay}>
      <View style={[styles.rowAccount, styles.webPadding, style]}>
        {item.isPending || !isEditingTransactions ? null : (<EditTransactionSelect selected={selected} isMultipleSelect={isMultipleSelect}/>)}
        <View style={styles.rowInner}>
          <View style={styles.imageContainer}>
            <View>
              <TransactionLogo R44 transaction={item} backgroundDark={backgroundDark}/>
              {bankLogoTransaction ? (<TransactionBankLogo id={item.accountId} isSmall/>) : null}
            </View>
          </View>
          <BlurAmountView enabled={blurValue} containerStyle={styles.textContainer}>
            <Text Text-16 numberOfLines={1} ellipsizeMode="tail">
              {nameToDisplay}
            </Text>
            <TransactionAutoCreated transaction={item}/>
            {Boolean(notes) && (<Text TextThin-14 Gray numberOfLines={1} ellipsizeMode="tail">
                {notes}
              </Text>)}
            {Boolean(fee) && (<Text TextThin-14 Gray numberOfLines={1} ellipsizeMode="tail">
                Fees charged {getSymbolFromCurrency(firstCurrency)}
                {fee}
              </Text>)}
            {datePosition === 'left' && Boolean(date) && (<Text TextThin-14 Gray numberOfLines={1} ellipsizeMode="tail">
                {date}
              </Text>)}
            {Boolean(categoryPosition === 'left' &&
            item.category?.displayName &&
            showCategory) && (<Text TextThin-14 Gray numberOfLines={1} ellipsizeMode="tail">
                {item.category.displayName}
              </Text>)}
            {renderSwitching()}
            <SpaceUserAvatar userId={item.userId} xxSmall/>
          </BlurAmountView>
        </View>
        {hasUpgradeButton ? (<View style={styles.numberContainer}>
            <Button square brandReversed small title="Upgrade" onPress={onPressUpgrade}/>
          </View>) : (<BlurAmountView enabled={blurValue} containerStyle={isCrypto
                ? [styles.numberContainer, styles.cryptoNumberContainer]
                : styles.numberContainer}>
            <View style={styles.numberContainer}>
              {firstBalance > 0 ? (<Amount Blue Numbers-16 value={{ value: firstBalance, currency: firstCurrency }} showSign/>) : (<Amount OffBlack Numbers-16 value={{ value: firstBalance, currency: firstCurrency }} showSign/>)}
              {Boolean(nativeAmount) && secondBalance !== null && (<Amount Gray NumbersThin-14 value={{
                    value: secondBalance,
                    currency: isCrypto ? undefined : secondCurreny,
                }} showSign {...cryptoAmountProps}/>)}
              {Boolean(categoryPosition === 'right' &&
                item.category?.displayName &&
                showCategory) && (<Text TextThin-14 Gray numberOfLines={1} ellipsizeMode="tail" style={styles.categoryDisplayName}>
                  {item.category.displayName}
                </Text>)}
              {Boolean(datePosition === 'right' && Boolean(date)) && (<Text TextThin-14 Gray numberOfLines={1} ellipsizeMode="tail">
                  {date}
                </Text>)}
            </View>
          </BlurAmountView>)}
      </View>
    </TouchableHighlight>);
};
const styles = StyleSheet.create({
    imageContainer: {
        justifyContent: 'center',
    },
    rowAccount: {
        flexDirection: 'row',
        padding: rem(16),
        ...(isWeb && {
            paddingVertical: rem(16),
            paddingHorizontal: rem(8),
        }),
    },
    webPadding: {
        ...(isWeb && {
            paddingVertical: rem(16),
            paddingHorizontal: rem(8),
        }),
    },
    rowInner: {
        flexDirection: 'row',
        flex: 1,
        justifyContent: 'center',
    },
    textContainer: {
        flexDirection: 'column',
        justifyContent: 'center',
        paddingLeft: rem(8),
        marginLeft: rem(8),
        paddingRight: rem(7),
        marginRight: rem(7),
        flex: 1,
    },
    numberContainer: {
        justifyContent: 'center',
        alignItems: 'flex-end',
    },
    cryptoNumberContainer: {
        maxWidth: '40%',
    },
    categoryDisplayName: {
        maxWidth: rem(isWeb ? 150 : 100),
    },
});
export default RowTransaction;
